import DataTable from "react-data-table-component";
import { useEffect, useState } from "react";
import Button from "../../../../components/common/button";
import Switch from "../../../../components/common/Switch";
import AddSleepingEssentialsModal from "../../../../components/tv-app/sleep-concierge/add-sleeping-essentials-modal";
import {
  ISleepCategoryComponentProps,
  ISleepingEssentialsSubData,
  ITableColumn,
} from "../../../../utils/types";
import DeleteAndroidHome from "../../../../components/tv-app/delete-android-home";
import EditTableRowName from "../../../../components/tv-app/edit-name";
import DiscardSaveButtons from "../../../../components/tv-app/discard-save-buttons";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import useUpdateAndroidTVHomeOrder from "../../../../hooks/use-update-android-home-order";
import DraggableRow from "../../../../components/common/DragRow";
import { DragSvg } from "../../../../icons";
import { setDragOnlyInStaging } from "../../../../utils/helpers";
import { useGetUserPermission } from "../../../../context/AuthStaffUserContext";

type IFormData = {
  showSE: boolean;
  data: ISleepingEssentialsSubData[];
};

const SleepingEssentials = ({
  subData,
  show,
  onSave,
  loading,
  categoryName,
  type,
}: ISleepCategoryComponentProps<ISleepingEssentialsSubData[]>) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const getInitialFormData = () => {
    return {
      showSE: !!show,
      data: subData.sort((a, b) => a?.orderKey - b?.orderKey) ?? [],
    };
  };

  const [InitialFormData, setInitialFormData] = useState<IFormData>(
    getInitialFormData()
  );

  const methods = useForm<IFormData>({ defaultValues: InitialFormData });

  const {
    fields: sleepCatagoryFields,
    append: appendSleepCatagoryField,
    remove: removeSleepCatagoryField,
  } = useFieldArray({
    name: "data",
    control: methods.control,
    keyName: "customId",
  });

  const { reset, handleSubmit } = methods;
  const [sleepSettingData, setSleepSettingData] = useState(() => ({
    categoryName,
    items:
      [...(sleepCatagoryFields as ISleepingEssentialsSubData[])]?.sort(
        (a, b) => a.orderKey - b.orderKey
      ) ?? [], // Use subData directly or default to an empty array
  }));

  const { mutate: updateAndroidTVHomeOrder } = useUpdateAndroidTVHomeOrder();

  useEffect(() => {
    if (sleepCatagoryFields.length < subData.length) {
      const addedElement = subData[subData.length - 1];
      appendSleepCatagoryField({ ...addedElement });
      setInitialFormData((pre) => ({
        ...pre,
        data: [...pre.data, { ...addedElement }],
      }));
    }
  }, [subData]);

  useEffect(() => {
    setSleepSettingData((prev) => {
      const existingIds = new Set(prev.items.map((item) => item.id));
      const newItems = sleepCatagoryFields.filter(
        (item) => item.id && !existingIds.has(item.id)
      );
      return {
        ...prev,
        items: [...prev.items, ...newItems].sort(
          (a, b) => a?.orderKey - b?.orderKey
        ),
      };
    });
  }, [sleepCatagoryFields]);

  const getTableDataIndex = (id: string) => {
    return sleepCatagoryFields.findIndex((item) => item.id === id);
  };

  const onDropComplete = () => {
    const updatedItems = [...sleepSettingData.items].map((item, index) => ({
      ...item,
      orderKey: index + 1,
    }));
    setSleepSettingData((prev) => ({
      ...prev,
      items: updatedItems.sort((a, b) => a?.orderKey - b?.orderKey),
    }));

    const updatedDataWithOrderKey = updatedItems.map((item) => ({
      id: item.id,
      orderKey: item.orderKey,
    }));

    updateAndroidTVHomeOrder({
      categoryName,
      type,
      data: updatedDataWithOrderKey,
    });
  };

  const onSubmit = (submitData: IFormData) => {
    const orderKeyMap = new Map(
      sleepSettingData.items.map((item, index) => [
        item.id,
        item.orderKey || index + 1,
      ])
    );

    const mergedData = submitData.data
      .filter((item) => item?.id?.trim())
      .map((item) => ({
        ...item,
        orderKey: orderKeyMap.get(item.id) ?? item.orderKey,
      }));

    reset((prev) => ({
      ...prev,
      data: mergedData,
    }));
    onSave({
      show: submitData.showSE,
      subData: mergedData.sort((a, b) => a?.orderKey - b?.orderKey),
    });
  };

  const tableColumns: ITableColumn<ISleepingEssentialsSubData>[] =
    setDragOnlyInStaging<ITableColumn<ISleepingEssentialsSubData>>([
      {
        name: "",
        cell: (Row, index) => (
          <DraggableRow
            index={index}
            moveRow={moveRow}
            onDropComplete={onDropComplete}
          >
            <DragSvg
              height={24}
              width={24}
              className="h-6 w-6 cursor-grab flex-none"
            />
          </DraggableRow>
        ),
        sortable: true,
        width: "10%",
      },
      {
        name: "Name",
        cell: (Row) => {
          const index = getTableDataIndex(Row.id);
          if (index === -1) return null;
          return (
            <EditTableRowName
              name={`data.${index}.name`}
              disabled={!isEditPermission}
            />
          );
        },
        sortable: true,
        width: "30%",
      },
      {
        name: "Qty",
        // selector: (Row) => Row.qty,
        cell: (Row) => <span className="text-[#666672]">{Row.qty}</span>,
        sortable: true,
        center: true,
        width: "18%",
      },
      {
        name: "Hide/Show",
        cell: (Row) => (
          <Switch name={`data.${getTableDataIndex(Row.id)}.selected`} />
        ),
        sortable: true,
        center: true,
        width: "18%",
      },
      {
        name: "Action",
        cell: (Row) => (
          <DeleteAndroidHome
            categoryName={categoryName}
            type={type}
            deleteId={Row.id}
            disabled={!isEditPermission}
            removeField={() => {
              setInitialFormData((pre) => ({
                ...pre,
                data: sleepSettingData.items.filter(
                  (item) => item.id !== Row.id
                ),
              }));
              removeSleepCatagoryField(getTableDataIndex(Row.id));
              setSleepSettingData({
                ...sleepSettingData,
                items: sleepSettingData.items.filter(
                  (item) => item.id !== Row.id
                ),
              });
            }}
          />
        ),
        sortable: true,
        center: true,
        width: "18%",
      },
    ]);

  const moveRow = (fromIndex: number, toIndex: number) => {
    if (!sleepSettingData?.items) return;
    const globalFromIndex = (currentPage - 1) * pageSize + fromIndex;
    const globalToIndex = (currentPage - 1) * pageSize + toIndex;
    const updatedData = [...sleepSettingData.items];
    const [movedItem] = updatedData.splice(globalFromIndex, 1);
    updatedData.splice(globalToIndex, 0, movedItem);

    const reOrdered = updatedData.map((item, index) => ({
      ...item,
      orderKey: index + 1,
    }));

    setSleepSettingData({
      ...sleepSettingData,
      items: reOrdered,
    });
  };

  const { write: isEditPermission } = useGetUserPermission("tv-app");

  return (
    <FormProvider {...methods}>
      <div className="w-full mt-4">
        {/* <Switch name="showSE" label="Sleeping Essentials (Hide/Show)" /> */}
        <div className="flex items-center justify-between w-full">
          <h4 className="text-base leading-[39px] font-normal text-[#1E2F65] whitespace-nowrap">
            Sleeping Essentials
          </h4>
          <Button
            variant="primary"
            className="w-21"
            onClick={openModal}
            disabled={!isEditPermission}
          >
            Add New
          </Button>
        </div>
        <DndProvider backend={HTML5Backend}>
          <DataTable
            className="border-t mt-6"
            data={sleepSettingData.items}
            columns={tableColumns}
            striped={true}
            keyField="customId"
            pagination={sleepCatagoryFields.length > 10}
            customStyles={{
              headRow: { style: { paddingLeft: "36px" } },
              rows: { style: { paddingLeft: "36px" } },
            }}
            onChangePage={(page) => setCurrentPage(page)}
            onChangeRowsPerPage={(perPage) => setPageSize(perPage)}
          />
        </DndProvider>

        <DiscardSaveButtons
          onDiscard={() => {
            reset(getInitialFormData());
          }}
          onSave={handleSubmit(onSubmit)}
          loading={loading}
          className="mt-10"
        />

        <AddSleepingEssentialsModal
          isOpen={isModalOpen}
          closeModal={closeModal}
          categoryName={categoryName}
          type={type}
        />
      </div>
    </FormProvider>
  );
};

export default SleepingEssentials;
